import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DeliveryScheduleItemOut } from '../api/types'
import { chequeUncleared, clearCheque } from './deliveries'

export interface Notice {
  tone: 'info' | 'error'
  text: string
}

/** Selection, busy state and the result banner shared by the dispatch pages. */
export function useDispatch() {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)

  const toggle = useCallback((ids: string[], on: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  /** Everything a dispatch action can change: the inbox, the day plan, the board. */
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['dispatch'] })
    void queryClient.invalidateQueries({ queryKey: ['delivery-board'] })
  }, [queryClient])

  /**
   * Runs a batch of calls. Items are processed one at a time, so a failure can
   * leave earlier ones done — the lists are refreshed either way so the page
   * never shows stale state. Rethrows so a dialog can show the error in place.
   */
  const run = useCallback(
    async (success: string, action: () => Promise<void>) => {
      setBusy(true)
      setNotice(null)
      try {
        await action()
        setSelected(new Set())
        setNotice({ tone: 'info', text: success })
      } catch (err) {
        setNotice({ tone: 'error', text: err instanceof Error ? err.message : 'Something went wrong.' })
        throw err
      } finally {
        setBusy(false)
        refresh()
      }
    },
    [refresh],
  )

  return { selected, toggle, clear, busy, notice, setNotice, run }
}

/**
 * The app's cheque guard: a cheque sale can't be scheduled until the cheque
 * clears. The API only lets admins (and sales) mark it cleared, so an admin
 * is asked to confirm and a coordinator is told who can.
 */
export async function ensureChequesCleared(
  items: DeliveryScheduleItemOut[],
  isAdmin: boolean,
  setNotice: (notice: Notice) => void,
): Promise<boolean> {
  const uncleared = new Map<string, string>()
  for (const item of items) {
    if (chequeUncleared(item)) uncleared.set(item.invoice_id, item.invoice_reference ?? item.invoice_id)
  }
  if (!uncleared.size) return true

  const refs = [...uncleared.values()].join(', ')
  if (!isAdmin) {
    setNotice({
      tone: 'error',
      text: `The cheque on ${refs} hasn't cleared yet. Ask the salesperson or an admin to mark it cleared, then schedule it.`,
    })
    return false
  }
  const ok = window.confirm(
    `The cheque on ${refs} is not marked cleared.\n\nMark it cleared now? Only do this once the bank has cleared it.`,
  )
  if (!ok) return false
  for (const invoiceId of uncleared.keys()) await clearCheque(invoiceId)
  return true
}
